import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FullScreenLoading } from "../../components/ui/loader";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import { Edit3, Plus, Save, Trash2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import {
  getLeavePolicyDetails,
  LeavePolicyDetails,
  getLeaveEntitlements,
  createLeavePolicyDetail,
  updateLeavePolicyDetail,
  deleteLeavePolicyDetail,
  createLeavePolicy,
  updateLeavePolicy,
  SaveLeavePolicyPayload,
  getEmployeeTypes,
  getGrades,
  getLeaveTypes,
  getEmploymentStatuses,
} from "../../services/leavePolicyService";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import ConfirmDialog from "../../ui/ConfirmDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { InputFieldset } from "../../ui/inputFieldset";
import { Label } from "../../ui/label";
import { Switch } from "../../ui/switch";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../../ui/select";
import { SelectFieldset } from "../../ui/selectFieldset";
import { SelectTriggerUI } from "../../ui/selectTrigger";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";

const emptyPolicy = {
  policyName: "",
  employeeType: "",
  employmentStatus: "",
  grades: "",
};
const emptyEntitlement = {
  leaveType: "",
  annualQuota: "",
  monthlyAccrual: "",
  maxBalance: "",
  carryForward: "yes",
  carryLimit: "",
};
type PolicyForm = typeof emptyPolicy;
type DropdownOption = { value: string; label: string };
type EntitlementRow = {
  id: string;
  publicId: string;
  leaveType: string;
  leaveTypePublicId: string;
  annualQuota: number;
  monthlyAccrual: number;
  maxBalance: number;
  carryForward: boolean;
  carryLimit: number;
  isActive: boolean;
};
type EntitlementForm = typeof emptyEntitlement & { id?: string; publicId?: string; isActive?: boolean };

// Shared by /leave-policies/new (add) and /leave-policies/:policyId (edit).
export function LeavePolicyDetailsPage() {
  const navigate = useNavigate();
  const { policyId } = useParams();
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [savingEntitlement, setSavingEntitlement] = useState(false);
  const savingEntitlementRef = useRef(false);
  const [deletingEntitlement, setDeletingEntitlement] = useState(false);
  const deletingEntitlementRef = useRef(false);
  const { setPageHeader } = usePageHeader();
  const [policy, setPolicy] = useState<LeavePolicyDetails>();
  const [form, setForm] = useState<PolicyForm>(emptyPolicy);
  const [isActive, setIsActive] = useState(true);
  const [loadingPolicy, setLoadingPolicy] = useState(!!policyId);
  const [policyError, setPolicyError] = useState("");
  const [detailRefresh, setDetailRefresh] = useState(0);
  const [errors, setErrors] = useState<
    Partial<Record<keyof PolicyForm, string>>
  >({});
  const [leaveTypes, setLeaveTypes] = useState<DropdownOption[]>([]);
  const [loadingLeaveTypes, setLoadingLeaveTypes] = useState(true);
  const [employeeTypes, setEmployeeTypes] = useState<DropdownOption[]>([]);
  const [employmentStatuses, setEmploymentStatuses] = useState<
    DropdownOption[]
  >([]);
  const [grades, setGrades] = useState<DropdownOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [entitlements, setEntitlements] = useState<EntitlementRow[]>([]);
  const [loadingEntitlements, setLoadingEntitlements] = useState(false);
  const [entitlementsError, setEntitlementsError] = useState("");
  const [entitlementsRefresh, setEntitlementsRefresh] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setEntitlements([]);
    setEntitlementsError("");
    setLoadingEntitlements(!!policyId);
    if (!policyId) return;
    getLeaveEntitlements(policyId)
      .then((items) => {
        if (cancelled) return;
        setEntitlements(items.map((item) => ({
          id: item.publicId,
          publicId: item.publicId,
          leaveType: item.leaveTypeName,
          leaveTypePublicId: item.leaveTypePublicId,
          annualQuota: item.annualQuota,
          monthlyAccrual: item.monthlyAccrual,
          maxBalance: item.maxBalance,
          carryForward: item.carryForwardLimit > 0,
          carryLimit: item.carryForwardLimit,
          isActive: item.isActive,
        })));
      })
      .catch((error) => {
        if (cancelled) return;
        const message = getApiErrorMessage(error, "Failed to load leave entitlements.");
        setEntitlementsError(message);
        toast.error(message);
      })
      .finally(() => { if (!cancelled) setLoadingEntitlements(false); });
    return () => { cancelled = true; };
  }, [policyId, entitlementsRefresh]);


  // Load policy dropdown options on mount
  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      getEmployeeTypes(),
      getGrades(),
      getEmploymentStatuses(),
    ]).then(([types, levels, statuses]) => {
      if (cancelled) return;
      if (types.status === "fulfilled") {
        setEmployeeTypes(
          types.value.map((item) => ({
            value: item.publicId,
            label: item.typeName,
          })),
        );
      } else {
        toast.error(
          "Unable to load employee types. Reload the page to try again.",
        );
      }
      if (levels.status === "fulfilled") {
        setGrades(
          levels.value.map((item) => ({
            value: item.publicId,
            label: item.gradeName,
          })),
        );
      } else {
        toast.error("Unable to load grades. Reload the page to try again.");
      }
      if (statuses.status === "fulfilled") {
        setEmploymentStatuses(
          [...statuses.value]
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((item) => ({ value: item.publicId, label: item.statusName })),
        );
      } else {
        toast.error(
          "Unable to load employment statuses. Reload the page to try again.",
        );
      }
      setLoadingOptions(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const [modalOpen, setModalOpen] = useState(false);
  const [entitlement, setEntitlement] =
    useState<EntitlementForm>(emptyEntitlement);
  const [entitlementErrors, setEntitlementErrors] = useState<
    Partial<Record<keyof EntitlementForm, string>>
  >({});
  const [deleteTarget, setDeleteTarget] = useState<EntitlementRow | null>(
    null,
  );

  useEffect(() => {
    let cancelled = false;
    setPolicy(undefined);
    setForm(emptyPolicy);
    setIsActive(true);
    setErrors({});
    setPolicyError("");
    setLoadingPolicy(!!policyId);
    if (!policyId) return;
    getLeavePolicyDetails(policyId)
      .then((data) => {
        if (cancelled) return;
        setPolicy(data);
        setForm(policyToForm(data));
        setIsActive(data.isActive);
      })
      .catch((error) => {
        if (cancelled) return;
        const message = getApiErrorMessage(error, "Failed to load leave policy.");
        setPolicyError(message);
        toast.error(message);
      })
      .finally(() => { if (!cancelled) setLoadingPolicy(false); });
    return () => { cancelled = true; };
  }, [policyId, detailRefresh]);

  useEffect(() => {
    setPageHeader({
      title: policyId ? "Edit Policy" : "Add Policy",
      onBack: () => navigate("/leave-policies"),
    });
    return () => setPageHeader(null);
  }, [navigate, policyId, setPageHeader]);

  useEffect(() => {
    let cancelled = false;
    getLeaveTypes()
      .then((items) => {
        if (!cancelled)
          setLeaveTypes(
            items.map((item) => ({
              value: item.publicId,
              label: item.leaveName,
            })),
          );
      })
      .catch(() => {
        if (!cancelled)
          toast.error(
            "Unable to load leave types. Reload the page to try again.",
          );
      })
      .finally(() => {
        if (!cancelled) setLoadingLeaveTypes(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Retain the saved selection even when an active-only lookup omits it.
  const withSavedOption = (options: DropdownOption[], value?: string | null, label?: string | null) =>
    value && !options.some((option) => option.value === value)
      ? [...options, { value, label: label || value }]
      : options;
  const employeeTypeOptions = withSavedOption(employeeTypes, policy?.employeeTypePublicId, policy?.employeeTypeName);
  const employmentStatusOptions = withSavedOption(employmentStatuses, policy?.employmentStatusPublicId, policy?.employmentStatusName);
  const gradeOptions = withSavedOption(grades, policy?.gradePublicId, policy?.gradeName);

  const updatePolicy = (key: keyof PolicyForm, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const savePolicy = async () => {
    if (savingRef.current || loadingPolicy || policyError || (policyId && !policy)) return;
    const nextErrors: Partial<Record<keyof PolicyForm, string>> = {};
    (Object.keys(emptyPolicy) as Array<keyof PolicyForm>).forEach((key) => {
      if (!form[key].trim()) nextErrors[key] = "This field is required.";
    });
    const employeeType = employeeTypeOptions.find(
      (item) => item.value === form.employeeType,
    );
    const employmentStatus = employmentStatusOptions.find(
      (item) => item.value === form.employmentStatus,
    );
    const grade = gradeOptions.find((item) => item.value === form.grades);
    if (!employeeType) nextErrors.employeeType = "Choose an employee type.";
    if (!employmentStatus)
      nextErrors.employmentStatus = "Choose an employment status.";
    if (!grade) nextErrors.grades = "Choose a grade.";
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      toast.error("Complete the required policy fields.");
      return;
    }
    if (!employeeType || !grade || !employmentStatus) return;
    const publicId =
      policy?.publicId ||
      (policyId && !/^\d+$/.test(policyId) ? policyId : undefined);
    if (policyId && !publicId) {
      toast.error(
        "This policy has no backend public ID and cannot be updated.",
      );
      return;
    }
    const payload: SaveLeavePolicyPayload = {
      policyName: form.policyName.trim(),
      employeeTypePublicId: employeeType.value,
      employmentStatusPublicId: employmentStatus.value,
      gradePublicId: grade.value,
      isActive,
    };
    console.log("Save policy payload:", payload);
    savingRef.current = true;
    setSaving(true);
    try {
      const response = publicId
        ? await updateLeavePolicy(publicId, payload)
        : await createLeavePolicy(payload);
      const data = response?.data;
      const savedPublicId =
        publicId ||
        (typeof data === "string"
          ? data
          : data &&
              typeof data === "object" &&
              "publicId" in data &&
              typeof data.publicId === "string"
            ? data.publicId
            : undefined);
      if (savedPublicId) {
        setPolicy({
          publicId: savedPublicId,
          policyName: payload.policyName,
          employeeTypeName: employeeType.label,
          employeeTypePublicId: employeeType.value,
          employmentStatusName: employmentStatus.label,
          employmentStatusPublicId: employmentStatus.value,
          gradeName: grade.label,
          gradePublicId: grade.value,
          isActive: payload.isActive,
        });
      }
      toast.success(response?.message || "Leave policy saved.");
      if (!policyId) {
        navigate(
          savedPublicId
            ? `/leave-policies/${savedPublicId}`
            : "/leave-policies",
          { replace: true },
        );
      }
    } catch (error) {
      const message = getApiErrorMessage(error, "Failed to save leave policy.");
      toast.error(message);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const openEntitlement = (item?: EntitlementRow) => {
    setEntitlement(
      item
        ? {
            id: item.id,
            publicId: item.publicId,
            isActive: item.isActive,
            leaveType:
              item.leaveTypePublicId ||
              leaveTypes.find((option) => option.label === item.leaveType)
                ?.value ||
              "",
            annualQuota: String(item.annualQuota),
            monthlyAccrual: String(item.monthlyAccrual),
            maxBalance: String(item.maxBalance),
            carryForward: item.carryForward ? "yes" : "no",
            carryLimit: String(item.carryLimit),
          }
        : emptyEntitlement,
    );
    setEntitlementErrors({});
    setModalOpen(true);
  };

  const updateEntitlement = (key: keyof EntitlementForm, value: string) => {
    setEntitlement((current) => ({ ...current, [key]: value }));
    setEntitlementErrors((current) => ({ ...current, [key]: undefined }));
  };

  const saveEntitlement = async () => {
    if (savingEntitlementRef.current) return;
    if (!policy?.publicId) {
      toast.error("Save the policy before adding leave entitlements.");
      return;
    }
    const nextErrors: Partial<Record<keyof EntitlementForm, string>> = {};
    const savedEntitlement = entitlements.find((item) => item.publicId === entitlement.publicId);
    const leaveType = leaveTypes.find((option) => option.value === entitlement.leaveType)
      || (savedEntitlement ? { value: savedEntitlement.leaveTypePublicId || "", label: savedEntitlement.leaveType } : undefined);
    if (!leaveType) nextErrors.leaveType = "Choose a leave type.";
    const required =
      entitlement.carryForward === "yes"
        ? ["annualQuota", "monthlyAccrual", "maxBalance", "carryLimit"]
        : ["annualQuota", "monthlyAccrual", "maxBalance"];
    required.forEach((key) => {
      const field = key as keyof EntitlementForm;
      const value = Number(entitlement[field]);
      if (entitlement[field] === "" || !Number.isFinite(value) || value < 0)
        nextErrors[field] = "Enter a non-negative number.";
    });
    if (Object.keys(nextErrors).length) {
      setEntitlementErrors(nextErrors);
      toast.error("Complete the required entitlement fields.");
      return;
    }
    if (!leaveType) return;
    if (entitlement.id && !entitlement.publicId) {
      toast.error("This entitlement has no backend public ID and cannot be updated.");
      return;
    }
    const values = {
      leaveType: leaveType.label,
      leaveTypePublicId: leaveType.value,
      annualQuota: Number(entitlement.annualQuota),
      monthlyAccrual: Number(entitlement.monthlyAccrual),
      maxBalance: Number(entitlement.maxBalance),
      carryForward: entitlement.carryForward === "yes",
      carryLimit: entitlement.carryForward === "yes" ? Number(entitlement.carryLimit) : 0,
    };
    savingEntitlementRef.current = true;
    setSavingEntitlement(true);
    try {
      const payload = {
        annualQuota: values.annualQuota,
        maxBalance: values.maxBalance,
        carryForwardLimit: values.carryLimit,
        monthlyAccrual: values.monthlyAccrual,
        isActive: entitlement.isActive ?? true,
      };
      await (entitlement.id && entitlement.publicId
        ? updateLeavePolicyDetail(entitlement.publicId, payload)
        : createLeavePolicyDetail({
            ...payload,
            leavePolicyPublicId: policy.publicId,
            leaveTypePublicId: leaveType.value,
          }));
      setLoadingEntitlements(true);
      setEntitlementsRefresh((value) => value + 1);
      setModalOpen(false);
      toast.success(`Leave entitlement ${entitlement.id ? "updated" : "added"}.`);
    } catch (error) {
      const message = getApiErrorMessage(error, "Failed to save leave entitlement.");
      toast.error(message);
    } finally {
      savingEntitlementRef.current = false;
      setSavingEntitlement(false);
    }
  };


  const removeEntitlement = async () => {
    if (!policy || !deleteTarget || deletingEntitlementRef.current) return;
    if (!deleteTarget.publicId) {
      toast.error("This entitlement has no backend public ID and cannot be deleted.");
      return;
    }
    deletingEntitlementRef.current = true;
    setDeletingEntitlement(true);
    try {
      await deleteLeavePolicyDetail(deleteTarget.publicId);
      setLoadingEntitlements(true);
      setEntitlementsRefresh((value) => value + 1);
      setDeleteTarget(null);
      toast.success("Leave entitlement deleted.");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to delete leave entitlement."));
    } finally {
      deletingEntitlementRef.current = false;
      setDeletingEntitlement(false);
    }
  };


  return (
    <div className="space-y-6 pb-8 gap-2">
      {(loadingPolicy || loadingOptions || loadingLeaveTypes || loadingEntitlements || saving || savingEntitlement || deletingEntitlement) && createPortal(
        <div className="fixed inset-0 z-[100]">
          <FullScreenLoading title={deletingEntitlement ? "Deleting leave entitlement..." : savingEntitlement ? "Saving leave entitlement..." : saving ? "Saving leave policy..." : "Loading leave policy..."} />
        </div>, document.body,
      )}
      {policyError && (
        <div role="alert" className="text-sm text-red-600">
          {policyError}
          <Button variant="ghost" onClick={() => setDetailRefresh((value) => value + 1)}>Retry</Button>
        </div>
      )}
      <Card
        className="border-sky-200 bg-white shadow-sm mb-3"
        style={{ marginTop: 0 }}
      >
        <CardContent className="p-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-[repeat(4,minmax(0,1fr))_auto]">
            <FieldInput
              label="Policy Name"
              value={form.policyName}
              onChange={(value) => updatePolicy("policyName", value)}
              error={errors.policyName}
            />
            <FieldSelect
              label="Employee Type"
              value={form.employeeType}
              onChange={(value) => updatePolicy("employeeType", value)}
              options={employeeTypeOptions}
              disabled={loadingOptions || !employeeTypeOptions.length}
              placeholder={
                loadingOptions
                  ? "Loading employee types..."
                  : !employeeTypeOptions.length
                    ? "No employee types available"
                    : undefined
              }
              error={errors.employeeType}
            />
            <FieldSelect
              label="Employment Status"
              value={form.employmentStatus}
              onChange={(value) => updatePolicy("employmentStatus", value)}
              options={employmentStatusOptions}
              disabled={loadingOptions || !employmentStatusOptions.length}
              placeholder={
                loadingOptions
                  ? "Loading employment statuses..."
                  : !employmentStatusOptions.length
                    ? "No employment statuses available"
                    : undefined
              }
              error={errors.employmentStatus}
            />
            <FieldSelect
              label="Grades"
              value={form.grades}
              onChange={(value) => updatePolicy("grades", value)}
              options={gradeOptions}
              disabled={loadingOptions || !gradeOptions.length}
              placeholder={
                loadingOptions
                  ? "Loading grades..."
                  : !gradeOptions.length
                    ? "No grades available"
                    : undefined
              }
              error={errors.grades}
            />
            <div className="flex items-center gap-2 whitespace-nowrap">
              <Label htmlFor="leave-policy-active">Status</Label>
              <Switch
                id="leave-policy-active"
                checked={isActive}
                onCheckedChange={setIsActive}
                disabled={saving}
              />
              <span className="text-sm text-slate-600">
                {isActive ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
          <div className="mt-4 flex justify-end items-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate("/leave-policies")}
              className="border-gray-300 hover:bg-gray-300 text-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={savePolicy}
              disabled={loadingOptions || loadingPolicy || saving || !!policyError || (!!policyId && !policy)}
              className="bg-[#5B9BD5] text-white hover:bg-[#4A8CC7]"
            >
              <Save className="h-4 w-4" />
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </CardContent>
      </Card>
      {/* ENTITLEMENT SECTION START */}
      <Card
        className="overflow-hidden border-sky-200 bg-white shadow-sm"
        style={{ marginTop: 0 }}
      >
        <CardHeader className="flex w-full items-center justify-between gap-3 px-6 py-4">
          <CardTitle className="text-base font-semibold text-[#075985]">
            Add Entitlement
          </CardTitle>
          <Button
            disabled={!policy?.publicId || loadingPolicy}
            onClick={() => openEntitlement()}
            className="bg-[#5B9BD5] text-white hover:bg-[#4A8CC7]"
          >
            <Plus className="h-4 w-4" />
            Add Leaves
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <Table className="min-w-[780px]">
            <TableHeader>
              <TableRow className="bg-[#e3f2fd] hover:bg-[#e3f2fd]">
                {[
                  "Leave Type",
                  "Annual Quota",
                  "Monthly Accrual",
                  "Max Balance",
                  "Carry Forward",
                  "Carry Limit",
                ].map((title) => (
                  <TableHead
                    key={title}
                    className="px-6 py-3 text-xs font-bold text-[#075985]"
                  >
                    {title}
                  </TableHead>
                ))}
                <TableHead className="px-6 py-3 text-right text-xs font-bold text-[#075985]">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loadingPolicy || loadingEntitlements ? (
                <EmptyTableRow text="Loading leave entitlements..." />
              ) : policyError ? (
                <EmptyTableRow text="Unable to load the policy. Please retry above." />
              ) : !policyId && !policy ? (
                <EmptyTableRow text="Save the policy to add leave entitlements." />
              ) : entitlementsError ? (
                <TableRow><TableCell colSpan={7} className="text-center text-red-600">
                  {entitlementsError}
                  <Button variant="ghost" onClick={() => setEntitlementsRefresh((value) => value + 1)}>Retry</Button>
                </TableCell></TableRow>
              ) : entitlements.length === 0 ? (
                <EmptyTableRow text="No leave entitlements added yet." />
              ) : entitlements.map((item) => (
                <TableRow
                  key={item.id}
                  className="border-slate-200 hover:bg-sky-50/30"
                >
                  <TableCell className="px-6 py-3 text-xs text-slate-700">
                    {item.leaveType}
                  </TableCell>
                  <TableCell className="px-6 py-3 text-xs text-slate-600">
                    {item.annualQuota}
                  </TableCell>
                  <TableCell className="px-6 py-3 text-xs text-slate-600">
                    {item.monthlyAccrual}
                  </TableCell>
                  <TableCell className="px-6 py-3 text-xs text-slate-600">
                    {item.maxBalance}
                  </TableCell>
                  <TableCell className="px-6 py-3">
                    <Badge
                      className={
                        item.carryForward
                          ? "border border-emerald-300 bg-emerald-50 text-emerald-700"
                          : "border border-rose-300 bg-rose-50 text-rose-700"
                      }
                    >
                      {item.carryForward ? "Yes" : "No"}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-6 py-3 text-xs text-slate-600">
                    {item.carryLimit}
                  </TableCell>
                  <TableCell className="px-6 py-3">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => openEntitlement(item)}
                        className="h-8 w-8 text-[#5B9BD5] hover:bg-sky-50"
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setDeleteTarget(item)}
                        className="h-8 w-8 text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Leave Entitlement Modal */}
      <Dialog open={modalOpen} onOpenChange={(open) => { if (!savingEntitlementRef.current) setModalOpen(open); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a5f]">
              {entitlement.id ? "Edit Leaves" : "Add Leaves"}
            </DialogTitle>
            <DialogDescription>
              Configure quotas, accruals, caps, carryover, and cashout by leave
              type.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
            <FieldInput
              label="Policy Name"
              value={policy?.policyName || ""}
              onChange={() => undefined}
              disabled
              full
            />
            <FieldSelect
              label="Leave Type"
              value={entitlement.leaveType}
              onChange={(value) => updateEntitlement("leaveType", value)}
              options={withSavedOption(leaveTypes, entitlement.publicId ? entitlement.leaveType : undefined, entitlements.find((item) => item.publicId === entitlement.publicId)?.leaveType)}
              disabled={!!entitlement.id || loadingLeaveTypes || !leaveTypes.length}
              placeholder={
                loadingLeaveTypes
                  ? "Loading leave types..."
                  : !leaveTypes.length
                    ? "No leave types available"
                    : undefined
              }
              error={entitlementErrors.leaveType}
            />
            <FieldInput
              label="Annual Quota"
              value={entitlement.annualQuota}
              onChange={(value) => updateEntitlement("annualQuota", value)}
              placeholder="Total Leave"
              type="number"
              error={entitlementErrors.annualQuota}
            />
            <FieldInput
              label="Monthly Accrual"
              value={entitlement.monthlyAccrual}
              onChange={(value) => updateEntitlement("monthlyAccrual", value)}
              placeholder="Leaves per Month"
              type="number"
              error={entitlementErrors.monthlyAccrual}
            />
            <FieldInput
              label="Max Balance"
              value={entitlement.maxBalance}
              onChange={(value) => updateEntitlement("maxBalance", value)}
              placeholder="Allowed Leave Balance"
              type="number"
              error={entitlementErrors.maxBalance}
            />
            <div className="space-y-2">
              <Label>Carry Forward</Label>
              <RadioGroup
                value={entitlement.carryForward}
                onValueChange={(value) => {
                  updateEntitlement("carryForward", value);
                  if (value === "no") updateEntitlement("carryLimit", "");
                }}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="yes" id="carry-yes" />
                  <Label htmlFor="carry-yes" className="font-normal">
                    Yes
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="no" id="carry-no" />
                  <Label htmlFor="carry-no" className="font-normal">
                    No
                  </Label>
                </div>
              </RadioGroup>
            </div>
            <FieldInput
              label="Carry Limit"
              value={entitlement.carryLimit}
              onChange={(value) => updateEntitlement("carryLimit", value)}
              placeholder="No. of days"
              type="number"
              disabled={entitlement.carryForward === "no"}
              error={entitlementErrors.carryLimit}
            />
          </div>
          <DialogFooter>
            <Button variant="destructive" disabled={savingEntitlement} onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveEntitlement}
              disabled={savingEntitlement || loadingLeaveTypes || !leaveTypes.length}
              className="bg-[#5B9BD5] text-white hover:bg-[#4A8CC7]"
            >
              <Save className="h-4 w-4" />
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && !deletingEntitlementRef.current && setDeleteTarget(null)}
        title="Delete Leave Entitlement"
        description={<>Delete {deleteTarget?.leaveType} from this policy?</>}
        confirmText="Delete"
        onConfirm={removeEntitlement}
        loading={deletingEntitlement}
      />
    </div>
  );
}

const policyToForm = (policy: LeavePolicyDetails): PolicyForm => ({
  policyName: policy.policyName,
  employeeType: policy.employeeTypePublicId || "",
  employmentStatus: policy.employmentStatusPublicId || "",
  grades: policy.gradePublicId || "",
});
function EmptyTableRow({ text }: { text: string }) {
  return (
    <TableRow>
      <TableCell
        colSpan={7}
        className="px-6 py-10 text-center text-sm text-slate-500"
      >
        {text}
      </TableCell>
    </TableRow>
  );
}
function FieldInput({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  error,
  disabled,
  full,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: "text" | "number";
  error?: string;
  disabled?: boolean;
  full?: boolean;
}) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <InputFieldset
        label={label}
        required={!disabled}
        type={type}
        min={type === "number" ? 0 : undefined}
        step={type === "number" ? "any" : undefined}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="border-gray-300"
      />
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
function FieldSelect({
  label,
  value,
  onChange,
  options,
  error,
  disabled,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<string | DropdownOption>;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <div>
      <SelectFieldset label={label} required className="border-gray-300">
        <Select value={value} onValueChange={onChange} disabled={disabled}>
          <SelectTriggerUI>
            <SelectValue placeholder={placeholder || `Choose ${label}`} />
          </SelectTriggerUI>
          <SelectContent>
            {options.map((option) => (
              <SelectItem
                key={typeof option === "string" ? option : option.value}
                value={typeof option === "string" ? option : option.value}
              >
                {typeof option === "string" ? option : option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SelectFieldset>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
